import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';

interface ToolCardProps {
  emoji: string;
  title: string;
  description: string;
  to: string;
  delay?: number;
}

function ToolCard({ emoji, title, description, to, delay = 0 }: ToolCardProps) {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileTap={{ scale: 0.98 }}
      onClick={() => {
        hapticFeedback('light');
        navigate(to);
      }}
      className="w-full p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 hover:border-amber-500/30 transition-all text-left flex items-center gap-4"
    >
      <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
        <span className="text-2xl">{emoji}</span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-white">{title}</h3>
        <p className="text-sm text-gray-400 truncate">{description}</p>
      </div>
      <span className="text-gray-500">→</span>
    </motion.button>
  );
}

export function GMToolsPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();

  const tools = [
    {
      emoji: '📖',
      title: 'Принципы мастера',
      description: 'Основы ведения игры',
      to: '/gm/principles',
    },
    {
      emoji: '🎬',
      title: 'Ходы мастера',
      description: 'Реакции на действия игроков',
      to: '/gm/moves',
    },
    {
      emoji: '🏰',
      title: 'Фронты',
      description: 'Угрозы и приключения',
      to: '/gm/fronts',
    },
    {
      emoji: '⚔️',
      title: 'Монстры',
      description: 'Бестиарий существ',
      to: '/gm/monsters',
    },
    {
      emoji: '💎',
      title: 'Сокровища',
      description: 'Магические предметы и лут',
      to: '/gm/treasures',
    },
    {
      emoji: '🌍',
      title: 'Создание мира',
      description: 'Картография и локации',
      to: '/gm/world-building',
    },
    {
      emoji: '📝',
      title: 'Первая сессия',
      description: 'Чеклист и советы',
      to: '/gm/first-session',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => {
              hapticFeedback('light');
              navigate('/mode-select');
            }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Назад
          </button>
          <h1 className="text-lg font-bold text-white">Инструменты мастера</h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20"
        >
          <p className="text-amber-200 text-sm">
            🎲 Добро пожаловать, мастер! Здесь собраны все инструменты для ведения игры по Dungeon World.
          </p>
        </motion.div>

        {tools.map((tool, index) => (
          <ToolCard
            key={tool.to}
            {...tool}
            delay={0.05 * (index + 1)}
          />
        ))}
      </div>
    </div>
  );
}
